import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { FaEnvelope, FaExclamationCircle, FaArrowLeft } from "react-icons/fa";
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const navigate = useNavigate();
  const { resetPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError('');
      setLoading(true);

      // Basic email validation
      if (!email.includes('@')) {
        setError('Please enter a valid email address');
        return;
      }

      await resetPassword(email);
      setEmailSent(true);
      toast.success('Password reset email sent! Please check your inbox.');
    } catch (err: any) {
      console.error('Password reset error:', err);
      setError('Failed to send reset email. Please try again.');
      toast.error('Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 py-6 px-2 sm:px-4">
      <div className="w-full max-w-md space-y-8">
        <div className="text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-truth">Reset Password</h1>
          <p className="mt-2 text-gray-400">Enter your email to receive a reset link</p>
        </div>

        <Card className="bg-black/20 backdrop-blur-sm border-gray-800">
          <CardHeader>
            <CardTitle className="text-lg sm:text-xl">Forgot Password</CardTitle>
            <CardDescription className="text-xs sm:text-sm">
              We'll send you a link to reset your password
            </CardDescription>
          </CardHeader>

          {!emailSent ? (
            <form onSubmit={handleSubmit}>
              <CardContent className="space-y-4">
                {error && (
                  <div className="p-3 bg-red-900/30 border border-red-800 rounded-md flex items-center gap-2 text-sm">
                    <FaExclamationCircle className="text-red-500" />
                    <span>{error}</span>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <FaEnvelope className="text-gray-500" />
                    </div>
                    <Input
                      id="email"
                      type="email"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      required
                    />
                  </div>
                </div>
              </CardContent>

              <CardFooter className="flex flex-col space-y-4">
                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </Button>

                <Link
                  to="/auth/signin"
                  className="text-sm text-truth hover:underline flex items-center justify-center gap-2"
                >
                  <FaArrowLeft className="text-xs" />
                  Back to Sign In
                </Link>
              </CardFooter>
            </form>
          ) : (
            <CardContent className="space-y-6">
              <div className="flex justify-center">
                <div className="rounded-full bg-truth/10 p-6">
                  <FaEnvelope className="h-12 w-12 text-truth" />
                </div>
              </div>

              <div className="text-center">
                <p className="mb-2 text-sm sm:text-base">
                  A password reset link has been sent to:
                </p>
                <p className="font-semibold text-truth text-base sm:text-lg">
                  {email}
                </p>
              </div>

              <div className="bg-blue-900/20 border border-blue-800 rounded-md p-4 text-sm">
                <div className="flex items-start gap-2">
                  <FaExclamationCircle className="text-blue-400 mt-1" />
                  <div>
                    <p>Please check your inbox and click the reset link to set a new password.</p>
                    <p className="mt-2">If you don't see the email, check your spam folder.</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col space-y-4">
                <Button
                  onClick={() => setEmailSent(false)}
                  variant="outline"
                  className="w-full"
                >
                  Try Another Email
                </Button>

                <Link
                  to="/auth/signin"
                  className="text-sm text-truth hover:underline flex items-center justify-center gap-2"
                >
                  <FaArrowLeft className="text-xs" />
                  Back to Sign In
                </Link>
              </div>
            </CardContent>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword; 